import React from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import { WithMember } from "@quintype/components";
import { SocialFollow } from "../basic/social-follow/social-follow";
import { CustomLink } from "../custom-link";
import { ImageFallback } from "../basic/image-fallback";
import { Menu } from "./menu";
import { SvgHandler } from "../svg-handler/svg-handler";
import hamburgerIcon from "../../../assets/icons/hamburger.svg";
import closeIcon from "../../../assets/icons/social/close.svg";
import { CSSTransition } from "react-transition-group";
import { UserProfile } from "./user-profile";
import { SidebarTemplate } from "./sidebar";
import "./header-helper-components.m.css";

function getMenuData(menu, menuGroup) {
  const filteredMenu = menu.filter(item => item["menu-group-slug"] === menuGroup);
  if (!filteredMenu.length && menuGroup === "sidebar-menu") {
    return getMenuData(menu, "default");
  }
  return filteredMenu;
}

export const CtaWrapper = props => {
  const {
    socialLinks,
    publisherTheme,
    customCssClasses = "",
    isSingleLayer,
    enableAccesstype,
    socialFollowStyle
  } = props;

  const headerClass = isSingleLayer ? "single-layer-header" : "header";
  return (
    <>
      {enableAccesstype && (
        <CustomLink
          styleName={`${headerClass} ${customCssClasses}`}
          href="/subscription"
          data-test-id="header-subscribe"
        >
          Subscribe
        </CustomLink>
      )}
      <SocialFollow
        hasBackground={true}
        socialStyle={socialFollowStyle}
        socialLinks={socialLinks}
        color={publisherTheme["header_icon_color"] || "#000"}
        customCssClasses={`${headerClass} ${customCssClasses}`}
      />
    </>
  );
};
CtaWrapper.propTypes = {
  socialLinks: PropTypes.object,
  publisherTheme: PropTypes.object,
  customCssClasses: PropTypes.string,
  isSingleLayer: PropTypes.bool,
  socialStyle: PropTypes.string,
  enableAccesstype: PropTypes.bool,
  socialFollowStyle: PropTypes.string
};

export const LogoWrapper = props => {
  const secondaryLogo = get(props, ["headerSecondaryLogo"], "");
  return (
    <>
      <CustomLink
        href="/"
        callback={() => {
          props.closeAll();
          typeof props.windowScroll === "function" && props.windowScroll();
        }}
      >
        {secondaryLogo ? <img data-test-id="secondary_logo" src={secondaryLogo} alt="Logo" /> : <ImageFallback />}
      </CustomLink>
    </>
  );
};
LogoWrapper.propTypes = {
  windowScroll: PropTypes.func,
  closeAll: PropTypes.func,
  publisherTheme: PropTypes.object
};

export const Monogram = props => {
  const primaryLogo = get(props, ["primaryLogo"], "");
  return (
    <div id="monogram">
      {
        <CustomLink
          href="/"
          callback={() => {
            this.props.closeAll();
            typeof this.props.windowScroll === "function" && this.props.windowScroll();
          }}
        >
          {primaryLogo ? <img src={primaryLogo} alt="Logo" /> : <ImageFallback />}
        </CustomLink>
      }
    </div>
  );
};

export const SecondaryMenu = ({ menu, publisherTheme, toggleSidebar, closeAll, customCssClasses = "" }) => (
  <Menu
    menu={getMenuData(menu, "secondary-menu")}
    languageDirection={get(publisherTheme, ["language_direction"], "left")}
    count={publisherTheme["secondary_menu_count"] || 4}
    toggleSidebar={toggleSidebar}
    closeAll={closeAll}
    customCssClasses={`menu-row ${customCssClasses}`}
    type="secondary"
  />
);
SecondaryMenu.propTypes = {
  menu: PropTypes.object,
  closeAll: PropTypes.func,
  publisherTheme: PropTypes.object,
  toggleSidebar: PropTypes.func,
  customCssClasses: PropTypes.string,
  type: PropTypes.string
};

export const DefaultMenu = ({ menu, publisherTheme, closeAll, customCssClasses = "", isSingleLayer }) => (
  <Menu
    menu={getMenuData(menu, "default")}
    languageDirection={get(publisherTheme, ["language_direction"], "left")}
    count={publisherTheme["primary_menu_count"] || 6}
    closeAll={closeAll}
    customCssClasses={`menu-row ${customCssClasses}`}
    type="default"
    isSingleLayer={isSingleLayer}
  />
);
DefaultMenu.propTypes = {
  menu: PropTypes.object,
  closeAll: PropTypes.func,
  publisherTheme: PropTypes.object,
  customCssClasses: PropTypes.string,
  isSingleLayer: PropTypes.bool,
  type: PropTypes.string
};

export const Sidebar = props => {
  const {
    dataTestId,
    toggleSidebar,
    closeAll,
    publisherName,
    menu,
    socialLinks,
    publisherTheme,
    header = {},
    headerClass = "",
    pageBuilderConfig,
    socialFollowStyle
  } = props;
  const isSidebarVisible = header.isSidebarVisible;

  return (
    <>
      <div data-test-id={dataTestId} onClick={toggleSidebar}>
        <div>
          {!isSidebarVisible ? (
            <SvgHandler xlinkHref={hamburgerIcon} width="24" height="24" svgWrapperClazzName="svg-wrapper" />
          ) : (
            <SvgHandler
              xlinkHref={closeIcon}
              clazzName="closeIcon"
              width="24"
              height="24"
              svgWrapperClazzName="svg-wrapper"
            />
          )}
        </div>
        {
          <CSSTransition in={isSidebarVisible} timeout={{ enter: 300, exit: 500 }} classNames="side-bar-transition">
            <div>
              <SidebarTemplate
                customCssClasses=""
                isSidebarVisible={isSidebarVisible}
                closeSidebar={toggleSidebar}
                closeAll={closeAll}
                menuItems={getMenuData(menu, "sidebar-menu")}
                publisherName={publisherName}
                languageDirection={get(publisherTheme, ["language_direction"], "left")}
                socialLinks={socialLinks}
                publisherTheme={publisherTheme}
                headerClass={headerClass}
                pageBuilderConfig={pageBuilderConfig}
                socialStyle={socialFollowStyle}
              />
            </div>
          </CSSTransition>
        }
      </div>
    </>
  );
};
Sidebar.propTypes = {
  header: PropTypes.object,
  toggleSidebar: PropTypes.func,
  menu: PropTypes.object,
  closeAll: PropTypes.func,
  isSidebarVisible: PropTypes.bool,
  publisherName: PropTypes.string,
  socialLinks: PropTypes.object,
  publisherTheme: PropTypes.string,
  dataTestId: PropTypes.string,
  headerClass: PropTypes.string,
  pageBuilderConfig: PropTypes.object,
  socialFollowStyle: PropTypes.object
};

export const Login = props => {
  const { openLoginTemplate, closeLoginTemplate, headerType = "" } = props;
  return (
    <WithMember>
      {({ member, logout, isLoading, checkForMemberUpdated }) => (
        <UserProfile
          openLoginTemplate={openLoginTemplate}
          closeLoginTemplate={closeLoginTemplate}
          member={member}
          logout={logout}
          isLoading={isLoading}
          checkForMemberUpdated={checkForMemberUpdated}
          customCssClasses={headerType}
        />
      )}
    </WithMember>
  );
};

Login.propTypes = {
  openLoginTemplate: PropTypes.bool,
  closeLoginTemplate: PropTypes.bool,
  headerType: PropTypes.string
};
